import { useQuery } from '@tanstack/react-query'
import { retrieveLogs, saveHostRelationship, saveSettings, settingsQuery, type DeviceSettingsPage } from '../api/devices'
import { CheckField, FormActions, FormCaption, FormRow, HalfField } from '../ui/FormGrid'
import { toast } from '../ui/notify'
import { Spinner } from '../ui/Spinner'
import type { ScreenProps } from './screens'

/**
 * Fila Settings - inventoree/devices/others/deviceSettings.php: User, Area,
 * Office, Group, Classified info, Agent install settings, AD Relay, Host and
 * virtual machines, Retrieve logs.
 *
 * Toate trei scriu, ca in deviceSettings-1.js: intreaba "Are you sure?", apoi
 * POST /api/v2/devices/{id}/settings, /host-link si /retrieve-logs, cu aceleasi
 * nume de campuri. Inventoree::updateSettings / updateHostRelationship /
 * retrieveLogsRequest nu mai exista in portalul vechi, deci butoanele nu au
 * unde altundeva sa scrie.
 *
 * ASEZAREA e cea comuna (`ui/FormGrid.tsx`), de pe 10 septembrie 2026: eticheta
 * DEASUPRA campului, doua coloane de aceeasi latime, bifa langa cuvantul ei.
 *
 * Inainte, cele trei formulare erau `form-horizontal` cu eticheta la stanga pe
 * trei coloane din douasprezece si campul intins pe restul - masurat 1133px
 * latime pentru fiecare select, la 1600px fereastra - iar fiecare eticheta
 * statea intr-un `<span class="color">`, pe care `_layout.scss` il deseneaza ca
 * pe o PASTILA GRI (#DDD, 25px inaltime, taiata cu trei puncte). In Settings
 * pastila aia e stinsa de `od_console.css`; aici, unde clasa `od-console` nu e
 * pusa, se vedea. Span-ul pleaca, deci, nu se stinge: e mai putin markup, nu
 * mai mult CSS.
 *
 * CE NU S-A SCHIMBAT: ce campuri sunt, in ce ordine, cum se numesc si ce
 * trimit. Cele trei bife isi pastreaza `name` si `value="1"`, fiindca
 * formularul se citeste cu `FormData`, exact ca `ajaxForm` in portal.
 */

export function DeviceSettings({ id }: ScreenProps) {
  const { data, error } = useQuery(settingsQuery(id))
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />
  return <Settings page={data} />
}

/** Campurile formularului, ca ce trimitea ajaxForm. */
function fieldsOf(form: HTMLFormElement): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [key, value] of new FormData(form).entries()) {
    if (typeof value === 'string') out[key] = value
  }
  return out
}

function Settings({ page }: { page: DeviceSettingsPage }) {
  const form = page.form
  /*
   * Tot formularul ramane NECONTROLAT, ca inainte: starea sta in DOM si
   * `FormData` o citeste la trimitere. Bifele trec prin `CheckField` cu
   * `defaultChecked`, deci nu si-au schimbat felul - doar locul in pagina.
   */
  return (
    <div className="device-settings">
      <div className="notifications-box"></div>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Device</strong> settings
        </h4>
      </header>
      <div className="panel-body">
        <form
          className="updateDeviceSettings"
          method="post"
          onSubmit={(event) => {
            event.preventDefault()
            if (!window.confirm('Are you sure?')) return
            saveSettings(page.device.id, fieldsOf(event.currentTarget))
              .then(() => toast('Settings saved!', 'Success', 'success'))
              .catch(() => toast('Please try again.', 'Error', 'error'))
          }}
        >
          <input type="hidden" name="device_unique" value={page.device.id} required />

          <FormRow>
            <HalfField
              label="User"
              htmlFor="ds_device_owner"
              help={
                <>
                  <i className="fas fa-info-circle"></i> If user is not selected the device goes to unnasigned
                </>
              }
            >
              <select id="ds_device_owner" name="device_owner" className="form-control" defaultValue={form.ownerId || ''}>
                <option value="">Select...</option>
                {page.owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </HalfField>
            <HalfField label="Area" htmlFor="ds_device_area">
              <select id="ds_device_area" name="device_area" className="form-control" defaultValue={form.areaId || ''}>
                <option value="">Select...</option>
                {page.areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </HalfField>
          </FormRow>

          <FormRow>
            <HalfField label="Office" required htmlFor="ds_device_office">
              <select
                id="ds_device_office"
                name="device_office"
                className="form-control"
                defaultValue={form.officeId || ''}
                required
              >
                <option value="">Select...</option>
                {page.offices.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </HalfField>
            <HalfField label="Group" htmlFor="ds_device_group">
              <select id="ds_device_group" name="device_group" className="form-control" defaultValue={form.groupId || ''}>
                <option value="">Select...</option>
                {page.groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </HalfField>
          </FormRow>

          <FormRow>
            <HalfField label="Classified info" htmlFor="ds_device_classified_info">
              <textarea
                id="ds_device_classified_info"
                className="form-control"
                name="device_classified_info"
                defaultValue={form.classified}
              />
            </HalfField>
          </FormRow>

          {/*
            "Agent install settings" ramane scris: fara el, "Auto update" si
            "Beta version" nu mai spun a cui actualizare e vorba. E aceeasi
            eticheta, mutata pe randul ei - vezi `FormCaption`.
          */}
          <FormCaption>Agent install settings</FormCaption>
          <FormRow>
            <CheckField
              id="agent_auto_update"
              name="agent_auto_update"
              value="1"
              className="iCheckSettings"
              label="Auto update"
              defaultChecked={form.autoUpdate}
            />
            <CheckField
              id="agent_beta_version"
              name="agent_beta_version"
              value="1"
              className="iCheckSettings"
              label="Beta version"
              defaultChecked={form.beta}
            />
          </FormRow>

          <FormRow>
            <CheckField
              id="ad_relay"
              name="ad_relay"
              value="1"
              className="iCheckSettings"
              label="Set as AD Relay"
              defaultChecked={form.adRelay}
            />
          </FormRow>

          <FormActions>
            <button type="submit" className="btn btn-theme">
              Update
            </button>
          </FormActions>
        </form>
      </div>

      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Host</strong> and virtual machines
        </h4>
      </header>
      <div className="panel-body">
        <form
          className="updateHostRelationship"
          method="post"
          onSubmit={(event) => {
            event.preventDefault()
            if (!window.confirm('Are you sure?')) return
            const data = new FormData(event.currentTarget)
            saveHostRelationship(page.device.id, {
              host_type: String(data.get('host_type') ?? ''),
              host_device_id: String(data.get('host_device_id') ?? ''),
              guest_ids: data.getAll('guest_ids[]').map((v) => String(v)),
            })
              .then(() => toast('Host relationship saved!', 'Success', 'success'))
              .catch((err) => toast(err instanceof Error ? err.message : 'Please try again.', 'Error', 'error'))
          }}
        >
          <input type="hidden" name="device" value={page.device.id} />

          <FormRow>
            <HalfField label="Host type" htmlFor="ds_host_type">
              <select id="ds_host_type" name="host_type" className="form-control" defaultValue={page.host.host_type}>
                <option value="">Select...</option>
                {page.host.types.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.label}
                  </option>
                ))}
              </select>
            </HalfField>
            <HalfField
              label="Host"
              htmlFor="ds_host_device_id"
              help={
                <>
                  <i className="fas fa-info-circle"></i> The inventoried machine this one runs on. Leave empty for a host
                  or for bare metal.
                </>
              }
            >
              <select
                id="ds_host_device_id"
                name="host_device_id"
                className="form-control selectpicker"
                defaultValue={page.host.host_device_id || ''}
                title="Select..."
              >
                <option value="">None — this machine is not a guest</option>
                {page.host.choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </HalfField>
          </FormRow>

          <FormRow>
            <HalfField
              label="Virtual machines"
              htmlFor="ds_guest_ids"
              help={
                <>
                  <i className="fas fa-info-circle"></i> Machines that run on this host. The same link as Host, the other
                  way around.
                </>
              }
            >
              <select
                id="ds_guest_ids"
                name="guest_ids[]"
                className="form-control selectpicker"
                multiple
                defaultValue={page.host.guests.map((g) => String(g.id))}
                title="Select machines hosted here"
              >
                {page.host.choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </HalfField>
          </FormRow>

          <FormActions>
            <button type="submit" className="btn btn-theme">
              Update
            </button>
          </FormActions>
        </form>
      </div>

      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Retrieve</strong> logs
        </h4>
      </header>
      <div className="panel-body">
        <form
          className="retrieveLogs"
          method="post"
          onSubmit={(event) => {
            event.preventDefault()
            if (!window.confirm('Are you sure?')) return
            const sent = event.currentTarget
            const next = String(new FormData(sent).get('logs_email') || '').trim()
            retrieveLogs(page.device.id, next)
              .then(() => {
                sent.reset()
                toast('Request sent!', 'Success', 'success')
              })
              .catch((err) => toast(err instanceof Error ? err.message : 'Error', 'Error', 'error'))
          }}
        >
          <input type="hidden" name="device_unique" value={page.device.id} required />

          <FormRow>
            <HalfField label="Email" required htmlFor="ds_logs_email">
              <input
                id="ds_logs_email"
                className="form-control"
                type="email"
                defaultValue=""
                name="logs_email"
                required
              />
            </HalfField>
          </FormRow>

          <FormActions>
            <button type="submit" className="btn btn-theme">
              Send request
            </button>
          </FormActions>
        </form>
      </div>
    </div>
  )
}
